import React, { useState } from "react";
import { PasswordInput, PasswordStrength } from "../components/PasswordField";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Flame, Mail, Phone, ArrowLeft, CheckCircle2, Lock } from "lucide-react";

const inputCls =
  "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all pl-11 pr-4 py-3 text-base";
const btnCls =
  "w-full rounded-full py-3 font-semibold text-white shadow-sm transition-all hover:brightness-105 disabled:opacity-60";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [method, setMethod] = useState("phone"); // phone is the default login
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);      // email link sent
  const [codeSent, setCodeSent] = useState(false); // sms code sent
  const [done, setDone] = useState(false);       // password reset complete

  const submitEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin: window.location.origin });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const sendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password-sms", { phone });
      setCodeSent(true);
      toast.success("If that number is registered, a reset code has been texted to it.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const resetWithCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/reset-password-sms", { phone, code, new_password: newPassword });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[100dvh] grid lg:grid-cols-2" style={{ background: "var(--sj-bg)" }}>
      <div className="relative hidden lg:block" style={{ background: "linear-gradient(140deg, #063B30, #0B5E4E)" }}>
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center"><Flame className="h-6 w-6" /></div>
            <span className="font-head font-extrabold text-lg">Streams of Joy</span>
          </div>
          <div>
            <div className="text-white/80 text-sm font-semibold uppercase tracking-[0.3em] mb-3">El Roi</div>
            <h1 className="font-head text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight">The God That Sees Me</h1>
            <p className="mt-5 text-white/90 text-lg max-w-md font-semibold">What God cannot do does not exist!</p>
          </div>
          <p className="text-white/70 text-sm">Streams of Joy Erith London · Church Management System</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-5 py-8 sm:p-10">
        <div className="w-full max-w-md sj-fade-up">
          <Link to="/login" data-testid="back-to-login-link" className="inline-flex items-center gap-2 text-sm font-semibold mb-8 hover:underline" style={{ color: "var(--sj-primary)" }}>
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>

          {done ? (
            <div data-testid="reset-done">
              <div className="h-12 w-12 rounded-xl flex items-center justify-center mb-5" style={{ background: "#17A88A1A" }}>
                <CheckCircle2 className="h-7 w-7" style={{ color: "var(--sj-secondary)" }} />
              </div>
              <h2 className="font-head text-3xl font-extrabold tracking-tight" style={{ color: "var(--sj-text)" }}>Password updated</h2>
              <p className="mt-3" style={{ color: "var(--sj-text-2)" }}>You can now sign in with your telephone number and new password.</p>
              <button onClick={() => navigate("/login")} className={`${btnCls} mt-8`} style={{ background: "var(--sj-primary)" }} data-testid="go-to-login-button">Go to sign in</button>
            </div>
          ) : sent ? (
            <div data-testid="forgot-password-sent">
              <div className="h-12 w-12 rounded-xl flex items-center justify-center mb-5" style={{ background: "#17A88A1A" }}>
                <CheckCircle2 className="h-7 w-7" style={{ color: "var(--sj-secondary)" }} />
              </div>
              <h2 className="font-head text-3xl font-extrabold tracking-tight" style={{ color: "var(--sj-text)" }}>Check your email</h2>
              <p className="mt-3" style={{ color: "var(--sj-text-2)" }}>
                If an account exists for <span className="font-semibold">{email}</span>, we've sent a link to reset your password. The link expires in 1 hour.
              </p>
              <Link to="/login" className={`${btnCls} mt-8 inline-block text-center`} style={{ background: "var(--sj-primary)" }} data-testid="return-to-login-button">Return to sign in</Link>
            </div>
          ) : (
            <>
              <h2 className="font-head text-3xl font-extrabold tracking-tight" style={{ color: "var(--sj-text)" }}>Forgot password?</h2>
              <p className="mt-2 mb-5" style={{ color: "var(--sj-text-2)" }}>Reset it with your telephone number, or by email if you have one on file.</p>

              <div className="grid grid-cols-2 gap-2 mb-6 p-1 rounded-xl" style={{ background: "var(--sj-peach)" }}>
                <button type="button" data-testid="reset-method-phone" onClick={() => setMethod("phone")} className="rounded-lg py-2 text-sm font-semibold transition-colors" style={{ background: method === "phone" ? "#fff" : "transparent", color: method === "phone" ? "var(--sj-primary)" : "var(--sj-text-2)" }}>Text me a code</button>
                <button type="button" data-testid="reset-method-email" onClick={() => setMethod("email")} className="rounded-lg py-2 text-sm font-semibold transition-colors" style={{ background: method === "email" ? "#fff" : "transparent", color: method === "email" ? "var(--sj-primary)" : "var(--sj-text-2)" }}>Email me a link</button>
              </div>

              {method === "email" ? (
                <form onSubmit={submitEmail} className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "var(--sj-text-3)" }} />
                    <input data-testid="forgot-email-input" required type="email" inputMode="email" autoComplete="email" autoCapitalize="none" spellCheck="false" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
                  </div>
                  <button data-testid="forgot-submit-button" disabled={loading} className={btnCls} style={{ background: "var(--sj-primary)" }}>{loading ? "Sending..." : "Send reset link"}</button>
                </form>
              ) : !codeSent ? (
                <form onSubmit={sendCode} className="space-y-4">
                  <div className="relative">
                    <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "var(--sj-text-3)" }} />
                    <input data-testid="forgot-phone-input" required type="tel" inputMode="tel" autoComplete="tel" placeholder="Telephone number" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} />
                  </div>
                  <button data-testid="send-code-button" disabled={loading} className={btnCls} style={{ background: "var(--sj-primary)" }}>{loading ? "Sending..." : "Text me a reset code"}</button>
                </form>
              ) : (
                <form onSubmit={resetWithCode} className="space-y-4" data-testid="reset-code-form">
                  <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>Enter the 6-digit code we texted to <span className="font-semibold">{phone}</span> and choose a new password.</p>
                  <div className="relative">
                    <Lock className="absolute z-10 left-3.5 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "var(--sj-text-3)" }} />
                    <input data-testid="reset-code-input" required inputMode="numeric" maxLength={6} placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className={`${inputCls} tracking-[0.4em] font-semibold`} />
                  </div>
                  <div className="relative">
                    <Lock className="absolute z-10 left-3.5 top-1/2 -translate-y-1/2 h-5 w-5" style={{ color: "var(--sj-text-3)" }} />
                    <PasswordInput testid="reset-new-password-input" required autoComplete="new-password" minLength={6} placeholder="New password (min 6 characters)" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputCls} />
                  </div>
                  <PasswordStrength value={newPassword} testid="forgot-strength" />
                  <button data-testid="reset-code-submit" disabled={loading} className={btnCls} style={{ background: "var(--sj-primary)" }}>{loading ? "Updating..." : "Reset password"}</button>
                  <button type="button" data-testid="resend-code-button" onClick={() => setCodeSent(false)} className="w-full text-sm font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>Use a different number / resend code</button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
